import { Author, asUserAuthor } from 'domain/shared/author';
import React, { useState } from 'react';
import { CompanyUpdate } from 'types/content';
import { UserProfile } from 'types/user';
import EditUpdateDialog from './EditUpdateDialog';
import { ICompany } from 'types/company';

import { getOrUndefined } from 'util/resource';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import CompanyUpdateMenu from 'domain/Updates/shared/Show/Menu/CompanyUpdateMenu';
import ShowUpdate, { ViewType } from 'domain/Updates/shared/Show';
import { companyUpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import { companyUrls } from 'urls';

interface Props {
  className?: string;
  update: CompanyUpdate;
  creator?: UserProfile;
  company: ICompany;
  onArchive: (contentId: string) => void;
  onDelete?: () => void;
  canEditPost: boolean;
  onEdit: () => void;
  canPinToFeed?: boolean;
  author?: Author;
  viewType?: ViewType;
  headingActions?: JSX.Element;
  isAtProfile: boolean;
}

export default function Update(props: Props) {
  const { update, creator, company } = props;
  const [editUpdate, setEditUpdate] = useState(false);
  const [isPinned, setPinned] = useState<boolean>(update.isPinned || false);
  const { resource: userProfileResource } = useSelfUserProfile();
  const userProfile = getOrUndefined(userProfileResource);
  const [viewType, setViewType] = useState<ViewType>(props.viewType || 'preview');

  const updateIsPinned = (isPinned: boolean) => {
    setPinned(isPinned);
    props.onEdit();
  };

  const api = companyUpdateAPI(props.update.companyId);

  const updateLink = `${location.origin}${companyUrls.update.view(props.company.slug, props.update.update.id)}`;

  return (
    <>
      <ShowUpdate
        className={props.className}
        api={api}
        update={props.update.update}
        author={props.author ?? asUserAuthor(creator)}
        creator={props.creator}
        updateContext={{ type: 'company', companyId: props.company.id, visibility: props.update.visibility }}
        viewType={viewType}
        canEditUpdate={props.canEditPost}
        showIsPinned={isPinned && props.isAtProfile}
        shownIn="company"
        actions={
          <>
            <CompanyUpdateMenu
              api={api}
              update={update.update}
              updateUrl={updateLink}
              company={company}
              location={{ type: 'company', slug: props.company.slug }}
              authorId={userProfile?.cwUserId !== creator?.cwUserId ? creator?.cwUserId : undefined}
              onEdit={() => setEditUpdate(true)}
              canEditPost={props.canEditPost}
              onArchive={props.onArchive}
              onDelete={props.onDelete}
              setPinned={updateIsPinned}
              isPinned={isPinned}
              canPinToFeed={props.canPinToFeed}
              trackingKey="company-update-request-to-follow"
              isAtProfile={props.isAtProfile}
            />
            {props.headingActions}
          </>
        }
      />
      {editUpdate && company && (
        <EditUpdateDialog
          setActive={setEditUpdate}
          companyUpdate={props.update}
          user={userProfile}
          company={company}
          onComplete={() => {
            props.onEdit();
            setViewType('read more');
          }}
        />
      )}
    </>
  );
}
